import { API_ROUTES } from "../../configuration/RoutesApi"
import { EAccountPlansType } from "../../Enum/EAccountPlansType"
import { EBillingPeriodType } from "../../Enum/EBillingPeriodType"
import { EChargeMethodType } from "../../Enum/EChargeMethodType"
import { ERequestType } from "../../Enum/ERequestType"
import { GetRequest, PostRequest } from "../../services/ApiClientService"
import { useTranslation } from "react-i18next"
import { useLoad } from "../../context/useLoadContext"
import { useError } from "../../context/useErrorContext"
import { SubscriptionCommercialOrigin } from "../../Objects/CommercialReferral/SubscriptionCommercialOrigin"
import { SubscriptionPurchaseResult } from "../../Objects/Company/SubscriptionPurchaseResult"
import { SubscriptionPurchaseStatus } from "../../Objects/Company/SubscriptionPurchaseStatus"
import { PlanCouponValidationResult } from "../../Objects/Company/PlanCouponValidationResult"
import { ResellerCodeValidationResult } from "../../Objects/Company/ResellerCodeValidationResult"
import { EOrderPaymentProviderType } from "../../Enum/EOrderPaymentProviderType"

interface ICreateOrderOnlineCheckoutResponse {
  preferenceId: string
  initPoint: string
  expiresAtUtc: string
}

interface IOnlinePaymentProviderOption {
  providerType: EOrderPaymentProviderType
  isOnlinePaymentEnabled: boolean
  isInPersonPaymentEnabled: boolean
}

export const usePayment = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const safeRequest = async (callback: any) => {
    enableLoad()

    try {
      return await callback()
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)

      return null
    } finally {
      disableLoad()
    }
  }

  const cancelSubscriptionPlan = async (onSuccess: any) =>
    safeRequest(async () => {
      const response = await PostRequest(API_ROUTES.PAYMENT.SUBSCRIPTION_CANCEL, {})

      if (response.success) {
        onSuccess && onSuccess(response)
      }

      return response
    })

  const purchaseSubscription = async (
    eAccountPlansType: EAccountPlansType,
    periodType: EBillingPeriodType,
    payerEmail: string,
    method: EChargeMethodType,
    couponCode: string | null,
    commercialOrigin: SubscriptionCommercialOrigin,
    onSuccess: (result: SubscriptionPurchaseResult) => void,
  ) =>
    safeRequest(async () => {
      const response = await PostRequest(API_ROUTES.PAYMENT.SUBSCRIPTION_PURCHASE, {
        EAccountPlansType: eAccountPlansType,
        PeriodType: periodType,
        PayerEmail: payerEmail,
        Method: method,
        CouponCode: couponCode || null,
        CommercialOrigin: { ...commercialOrigin },
      })

      if (response.success) {
        onSuccess && onSuccess(new SubscriptionPurchaseResult(response.data ?? {}))
      }

      return response
    })

  const getSubscriptionPurchaseStatus = async (
    chargeId: number,
    onSuccess: (status: SubscriptionPurchaseStatus) => void,
  ) => {
    try {
      const response = await GetRequest<SubscriptionPurchaseStatus>(
        API_ROUTES.PAYMENT.SUBSCRIPTION_PURCHASE_STATUS(chargeId),
      )

      if (response.success) {
        onSuccess && onSuccess(new SubscriptionPurchaseStatus(response.data ?? {}))
      }

      return response
    } catch {
      return null
    }
  }

  const validatePlanCoupon = async (
    code: string,
    eAccountPlansType: EAccountPlansType,
    periodType: EBillingPeriodType,
    onSuccess: (result: PlanCouponValidationResult) => void,
  ) =>
    safeRequest(async () => {
      const response = await PostRequest(API_ROUTES.PAYMENT.SUBSCRIPTION_COUPON_VALIDATE, {
        Code: code,
        EAccountPlansType: eAccountPlansType,
        PeriodType: periodType,
      })

      if (response.success) {
        onSuccess && onSuccess(new PlanCouponValidationResult(response.data ?? {}))
      }

      return response
    })

  const validateResellerCode = async (code: string, onSuccess: (result: ResellerCodeValidationResult) => void) =>
    safeRequest(async () => {
      const response = await PostRequest(API_ROUTES.PAYMENT.SUBSCRIPTION_VALIDATE_RESELLER_CODE, { Code: code })

      if (response.success) {
        onSuccess && onSuccess(new ResellerCodeValidationResult(response.data ?? {}))
      }

      return response
    })

  const requestSubscriptionSupport = async (notes: string, onSuccess: any) =>
    safeRequest(async () => {
      const response = await PostRequest(API_ROUTES.PAYMENT.SUBSCRIPTION_SUPPORT, {
        notes,
      })

      if (response.success) {
        onSuccess && onSuccess(response)
      }

      return response
    })

  const manualActivateSubscription = async (
    subscriptionId: number,
    onSuccess: any,
    planEntityId?: number,
    planPeriodEntityId?: number,
  ) =>
    safeRequest(async () => {
      const response = await PostRequest(
        API_ROUTES.PAYMENT.SUBSCRIPTION_MANUAL_ACTIVATE(subscriptionId),
        { PlanEntityId: planEntityId ?? null, PlanPeriodEntityId: planPeriodEntityId ?? null },
        ERequestType.Admin,
      )

      if (response.success) {
        onSuccess && onSuccess(response)
      }

      return response
    })

  const getRestaurantOAuthLink = async () =>
    safeRequest(async () => await GetRequest<{ authorizationUrl: string }>(API_ROUTES.PAYMENT.RESTAURANT_OAUTH_LINK))

  const unlinkRestaurantOAuth = async (onSuccess?: () => void) =>
    safeRequest(async () => {
      const response = await PostRequest<boolean>(API_ROUTES.PAYMENT.RESTAURANT_OAUTH_UNLINK, {})

      if (response.success) {
        onSuccess && onSuccess()
      }

      return response
    })

  const createOrderOnlineCheckout = async (orderId: number, providerType?: EOrderPaymentProviderType) => {
    try {
      enableLoad()

      const response = await PostRequest<ICreateOrderOnlineCheckoutResponse>(
        API_ROUTES.PAYMENT.ORDER_ONLINE_CHECKOUT,
        { orderId, providerType },
        ERequestType.Customer,
      )

      if (response.success) {
        return response.data
      }

      throw new Error(response.message)
    } catch (e: any) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const getRestaurantPaymentProviders = async () => {
    try {
      const response = await GetRequest<IOnlinePaymentProviderOption[]>(
        API_ROUTES.PAYMENT.ORDER_ONLINE_CHECKOUT_PROVIDERS,
        ERequestType.Customer,
      )

      return response.success ? (response.data ?? []) : []
    } catch {
      return []
    }
  }

  return {
    purchaseSubscription,
    getSubscriptionPurchaseStatus,
    validatePlanCoupon,
    cancelSubscriptionPlan,
    getRestaurantOAuthLink,
    unlinkRestaurantOAuth,
    createOrderOnlineCheckout,
    getRestaurantPaymentProviders,
    requestSubscriptionSupport,
    manualActivateSubscription,
    validateResellerCode,
  }
}
